import React from 'react';
import PolicyLayout from '../components/PolicyLayout';
import SEOHead from '../components/SEOHead';
import { getSEOConfig } from '../utils/seoConfig';

const ConsentPolicyPage: React.FC = () => {
  const seoConfig = getSEOConfig('/consent');

  return (
    <>
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        keywords={seoConfig.keywords}
        ogType={seoConfig.ogType}
      />
      <PolicyLayout title="Consent Policy" lastUpdated="October 2025">
        <p>
          By creating an account or using GelWel, you consent to the collection and processing of your data
          as outlined in this policy and our Privacy Policy.
        </p>

        <h2>1. What You Consent To</h2>
        <p>
          When you use GelWel, you consent to:
        </p>
        <ul>
          <li>Collection and processing of your data as described in the Privacy Policy.</li>
          <li>Use of your content and activity for AI-based social matching.</li>
          <li>Receiving in-app notifications or optional marketing updates.</li>
          <li>Storage and processing of your photos, messages, and profile information.</li>
          <li>Use of your location data to improve matching and content visibility.</li>
        </ul>

        <h2>2. How We Obtain Consent</h2>
        <p>
          We obtain your consent in several ways:
        </p>

        <h3>Account Creation</h3>
        <p>
          When you create a GelWel account, you explicitly agree to our Terms of Use and Privacy Policy,
          which outline how we collect and use your information.
        </p>

        <h3>In-App Permissions</h3>
        <p>
          The app will request specific permissions for features like location access, camera access, and
          notifications. You can grant or deny these permissions at any time through your device settings.
        </p>

        <h3>Optional Features</h3>
        <p>
          For certain features like marketing communications or enhanced AI matching, we will ask for your
          explicit consent before enabling these services.
        </p>

        <h2>3. Types of Consent</h2>

        <h3>Essential Consent</h3>
        <p>
          Some data processing is necessary for GelWel to function, such as storing your account information
          and enabling messaging features. This consent is required to use the service.
        </p>

        <h3>Optional Consent</h3>
        <p>
          Other data processing is optional, such as:
        </p>
        <ul>
          <li>Marketing emails and promotional notifications</li>
          <li>Enhanced AI matching using additional data points</li>
          <li>Participation in surveys or research studies</li>
          <li>Sharing anonymized data for analytics improvements</li>
        </ul>
        <p>
          You can opt in or out of these optional features at any time.
        </p>

        <h2>4. Managing Your Consent</h2>
        <p>
          You have full control over your consent preferences:
        </p>

        <h3>Update Preferences</h3>
        <p>
          You can change your consent preferences at any time through the app's Settings menu under
          "Privacy & Consent."
        </p>

        <h3>Withdraw Consent</h3>
        <p>
          You may withdraw consent at any time by:
        </p>
        <ul>
          <li>Adjusting your privacy settings in the app</li>
          <li>Disabling specific permissions through your device settings</li>
          <li>Deleting your account (see Account Deletion Policy)</li>
          <li>Contacting support at <a href="mailto:paulfortuneltd@gmail.com">paulfortuneltd@gmail.com</a></li>
        </ul>

        <h2>5. Consent for Minors</h2>
        <p>
          GelWel is only available to users 18 years and older. We do not knowingly collect data from minors
          or seek consent from individuals under the age of 18.
        </p>

        <h2>6. Consequences of Withdrawing Consent</h2>
        <p>
          If you withdraw consent for essential data processing, you may no longer be able to use certain
          features or the entire GelWel service. For optional features, withdrawing consent will simply
          disable those specific features without affecting your core app experience.
        </p>

        <h2>7. Changes to Consent Requirements</h2>
        <p>
          If we make significant changes to how we collect or use your data that require new consent, we
          will notify you and request your updated consent before implementing the changes.
        </p>

        <h2>8. Contact</h2>
        <p>
          For questions about consent or to manage your consent preferences, contact:{' '}
          <a href="mailto:paulfortuneltd@gmail.com">paulfortuneltd@gmail.com</a>
        </p>
      </PolicyLayout>
    </>
  );
};

export default ConsentPolicyPage;
